/** Origine publique du Community Studio (prod). */
export const MARKETING_SITE_ORIGIN = 'https://studio.mydid.com';

/** Titres page d'accueil (45–60 caractères pour SEO). */
export const LANDING_PAGE_TITLES = {
  fr: 'myDid Community Studio | Veille e-réputation & IA',
  en: 'myDid Community Studio | Reputation & AI Visibility',
};

export const DEFAULT_LANDING_PAGE_TITLE = LANDING_PAGE_TITLES.fr;

export function getLandingPageTitle(lang = 'fr') {
  return lang === 'en' ? LANDING_PAGE_TITLES.en : LANDING_PAGE_TITLES.fr;
}

/** URL canonique de la page d'accueil (sans slash final, alignée sur l'URL auditée). */
export const LANDING_CANONICAL_URL = MARKETING_SITE_ORIGIN;

/**
 * Canonical homepage : EN = racine, FR = ?lang=fr (variante linguistique distincte pour le SEO).
 */
export function getLandingCanonicalUrl(lang = 'en') {
  if (lang === 'fr') return `${MARKETING_SITE_ORIGIN}/?lang=fr`;
  return LANDING_CANONICAL_URL;
}

const ORG_ID = `${MARKETING_SITE_ORIGIN}/#organization`;
const WEBSITE_ID = `${MARKETING_SITE_ORIGIN}/#website`;
const PRODUCT_ID = `${MARKETING_SITE_ORIGIN}/#product`;
const SOFTWARE_ID = `${MARKETING_SITE_ORIGIN}/#software`;
const WEBPAGE_ID = `${MARKETING_SITE_ORIGIN}/#webpage`;
const FAQ_ID = `${MARKETING_SITE_ORIGIN}/#faq`;
const VIDEO_ID = `${MARKETING_SITE_ORIGIN}/#how-it-works-video`;

/** Vidéos « How it works » embarquées sur la landing (FR / EN). */
export const LANDING_FEATURE_VIDEOS = {
  fr: {
    id: 'NEWeWviIsj0',
    name: 'myDid Community Studio — comment ça marche',
    description:
      'Découvrez comment myDid Community Studio surveille votre e-réputation, analyse le sentiment et propose des plans d’action prioritaires.',
    // Requis Google Video rich results (ISO 8601)
    uploadDate: '2026-07-28T00:00:00.000Z',
  },
  en: {
    id: '10FpUPoy27M',
    name: 'myDid Community Studio — how it works',
    description:
      'See how myDid Community Studio monitors brand reputation, analyzes sentiment, and delivers priority action plans.',
    uploadDate: '2026-07-28T00:00:00.000Z',
  },
};

export function getLandingFeatureVideo(lang = 'fr') {
  return lang === 'en' ? LANDING_FEATURE_VIDEOS.en : LANDING_FEATURE_VIDEOS.fr;
}

function buildVideoObject(inLanguage) {
  const video = getLandingFeatureVideo(inLanguage);
  const watchUrl = `https://www.youtube.com/watch?v=${video.id}`;
  const embedUrl = `https://www.youtube.com/embed/${video.id}`;
  return {
    '@type': 'VideoObject',
    '@id': VIDEO_ID,
    name: video.name,
    description: video.description,
    thumbnailUrl: [
      `https://i.ytimg.com/vi/${video.id}/maxresdefault.jpg`,
      `https://i.ytimg.com/vi/${video.id}/hqdefault.jpg`,
    ],
    contentUrl: watchUrl,
    embedUrl,
    url: watchUrl,
    uploadDate: video.uploadDate,
    inLanguage,
    isFamilyFriendly: true,
    publisher: { '@id': ORG_ID },
    isPartOf: { '@id': WEBPAGE_ID },
  };
}

function getLocalizedCopy(inLanguage) {
  const isEn = inLanguage === 'en';
  return {
    pageDescription: isEn
      ? 'Monitor brand mentions across web, social and AI answers, measure your Reputation Score, and get action plans to turn customers into ambassadors.'
      : 'Surveillez vos mentions web, social et IA, mesurez votre Reputation Score et recevez des plans d’action pour améliorer votre e-réputation.',
    orgDescription: isEn
      ? 'myDid helps brands measure, monitor, and improve their online reputation through community engagement and AI-powered insights.'
      : 'myDid aide les marques à mesurer, surveiller et améliorer leur réputation en ligne grâce à l’engagement communautaire et à l’intelligence artificielle.',
    softwareDescription: isEn
      ? 'myDid Community Studio helps brands monitor brand mentions across channels, generate AI-powered recommendations, and build a community of ambassadors.'
      : 'myDid Community Studio aide les marques à surveiller les mentions sur tous les canaux, générer des recommandations par IA et fédérer une communauté d’ambassadeurs.',
    productCategory: isEn ? 'Reputation management' : 'Gestion de la réputation',
    offerDescriptions: isEn
      ? {
        basic: 'Free community management plan with a one-time Reputation Score diagnostic.',
        growth: 'Recurring brand analysis every 24 hours with competitor monitoring.',
        leader: 'Recurring brand analysis every 6 hours with increased community capacity.',
      }
      : {
        basic: 'Gestion de communauté gratuite avec un diagnostic Reputation Score ponctuel.',
        growth: 'Analyse de marque toutes les 24 heures avec suivi des concurrents.',
        leader: 'Analyse de marque toutes les 6 heures avec davantage de capacité communautaire.',
      },
  };
}

/**
 * Construit le graphe JSON-LD Schema.org pour la page d'accueil (/).
 * @param {{ lang?: string, description?: string, faqItems?: Array<{ q: string, a: string }> }} options
 */
export function buildLandingJsonLd({
  lang = 'fr',
  title = '',
  description = '',
  faqItems = [],
} = {}) {
  const inLanguage = lang === 'en' ? 'en' : 'fr';
  const copy = getLocalizedCopy(inLanguage);
  const pageTitle = title || getLandingPageTitle(inLanguage);
  const pageDescription = description || copy.pageDescription;
  const videoObject = buildVideoObject(inLanguage);

  const graph = [
    {
      '@type': 'Organization',
      '@id': ORG_ID,
      name: 'myDid',
      url: MARKETING_SITE_ORIGIN,
      logo: `${MARKETING_SITE_ORIGIN}/favicon.png`,
      description: copy.orgDescription,
      sameAs: ['https://mydid.com/'],
    },
    {
      '@type': 'WebSite',
      '@id': WEBSITE_ID,
      url: `${MARKETING_SITE_ORIGIN}/`,
      name: 'myDid Community Studio',
      description: pageDescription,
      publisher: { '@id': ORG_ID },
      inLanguage: ['fr', 'en'],
      hasPart: [
        {
          '@type': 'WebPage',
          '@id': `${MARKETING_SITE_ORIGIN}/blog`,
          url: `${MARKETING_SITE_ORIGIN}/blog`,
          name: 'Blog',
        },
        {
          '@type': 'CollectionPage',
          '@id': `${MARKETING_SITE_ORIGIN}/presse#webpage`,
          url: `${MARKETING_SITE_ORIGIN}/presse`,
          name: inLanguage === 'en' ? 'Press' : 'Presse',
        },
      ],
      potentialAction: {
        '@type': 'SearchAction',
        target: `${MARKETING_SITE_ORIGIN}/search?q={search_term_string}`,
        'query-input': 'required name=search_term_string',
      },
    },
    {
      '@type': 'Product',
      '@id': PRODUCT_ID,
      name: 'myDid Community Studio',
      url: `${MARKETING_SITE_ORIGIN}/`,
      description: pageDescription,
      category: copy.productCategory,
      brand: {
        '@type': 'Brand',
        name: 'myDid',
      },
    },
    {
      '@type': 'SoftwareApplication',
      '@id': SOFTWARE_ID,
      name: 'myDid Community Studio',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'ALL',
      url: `${MARKETING_SITE_ORIGIN}/`,
      description: copy.softwareDescription,
      provider: { '@id': ORG_ID },
      offers: [
        {
          '@type': 'Offer',
          name: 'Basic',
          price: '0',
          priceCurrency: 'EUR',
          description: copy.offerDescriptions.basic,
          url: `${MARKETING_SITE_ORIGIN}/#price`,
          availability: 'https://schema.org/InStock',
        },
        {
          '@type': 'Offer',
          name: 'Growth',
          price: '79',
          priceCurrency: 'EUR',
          description: copy.offerDescriptions.growth,
          url: `${MARKETING_SITE_ORIGIN}/#price`,
          availability: 'https://schema.org/InStock',
          priceSpecification: {
            '@type': 'UnitPriceSpecification',
            price: '79',
            priceCurrency: 'EUR',
            billingDuration: 'P1M',
          },
        },
        {
          '@type': 'Offer',
          name: 'Leader',
          price: '159',
          priceCurrency: 'EUR',
          description: copy.offerDescriptions.leader,
          url: `${MARKETING_SITE_ORIGIN}/#price`,
          availability: 'https://schema.org/InStock',
          priceSpecification: {
            '@type': 'UnitPriceSpecification',
            price: '159',
            priceCurrency: 'EUR',
            billingDuration: 'P1M',
          },
        },
      ],
    },
    videoObject,
    {
      '@type': 'WebPage',
      '@id': WEBPAGE_ID,
      url: `${MARKETING_SITE_ORIGIN}/`,
      name: pageTitle,
      description: pageDescription,
      isPartOf: { '@id': ORG_ID },
      about: [{ '@id': PRODUCT_ID }, { '@id': SOFTWARE_ID }],
      video: { '@id': VIDEO_ID },
      primaryImageOfPage: videoObject.thumbnailUrl[0],
      inLanguage,
      potentialAction: {
        '@type': 'SearchAction',
        target: `${MARKETING_SITE_ORIGIN}/search?q={search_term_string}`,
        'query-input': 'required name=search_term_string',
      },
    },
  ];

  if (Array.isArray(faqItems) && faqItems.length > 0) {
    graph.push({
      '@type': 'FAQPage',
      '@id': FAQ_ID,
      url: `${MARKETING_SITE_ORIGIN}/#faq`,
      isPartOf: { '@id': WEBPAGE_ID },
      inLanguage,
      mainEntity: faqItems.map((item) => ({
        '@type': 'Question',
        name: item.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.a,
        },
      })),
    });
  }

  return {
    '@context': 'https://schema.org',
    '@graph': graph,
  };
}

/** Graphe JSON-LD par défaut (FR) pour le shell HTML statique. */
export const DEFAULT_LANDING_JSON_LD = buildLandingJsonLd({
  lang: 'fr',
  faqItems: [
    {
      q: 'En combien de temps vais-je voir des résultats concrets ?',
      a: 'Le diagnostic fournit immédiatement un état de référence. L’évolution dépend ensuite des actions mises en œuvre et se mesure au fil des actualisations prévues par votre plan.',
    },
    {
      q: 'Quels canaux myDid surveille-t-il ?',
      a: 'myDid surveille le web & la presse (y compris les forums), X (Twitter), LinkedIn, Instagram, YouTube, TikTok, Discord, Reddit, Google Avis, et les moteurs IA (ChatGPT, Perplexity, Gemini).',
    },
    {
      q: 'Comment myDid m\'aide-t-il concrètement ?',
      a: 'myDid analyse votre réputation en ligne, calcule votre Reputation Score marque (visibilité, sentiment, activité communauté, qualité des signaux, progression des actions) et propose des actions prioritaires. Chaque mention reçoit aussi un score d\'impact pour savoir quoi traiter en premier. L\'analyse récurrente suit votre plan (24 h Growth / 6 h Leader).',
    },
    {
      q: 'Est-ce adapté aux petites structures ?',
      a: 'Oui. Basic est gratuit pour la gestion de communauté (badges, quêtes). L\'analyse de marque récurrente et le suivi concurrents sont sur Growth ou Leader — vous pouvez commencer avec l\'essai Growth 30 jours après le diagnostic.',
    },
    {
      q: 'Que se passe-t-il après mon diagnostic gratuit ?',
      a: 'Vous recevez votre Reputation Score et les actions prioritaires. Sur Basic, il ne reste ensuite que la gestion de communauté. Pour continuer à analyser votre marque et vos concurrents, utilisez l\'essai Growth 30 jours puis abonnez-vous.',
    },
  ],
});
